import AsyncStorage from '@react-native-async-storage/async-storage';

import { API_URL } from '../config';
import { sessionLogger } from '../utils/logger';
import {
  getAuthToken,
  getDeviceTrustToken,
  migrateLegacyTokensIfNeeded,
  removeAuthToken,
  setAuthToken,
} from './auth/tokenStorage';

const REFRESH_THRESHOLD = 5 * 60 * 1000; // 5 minutes before expiry

class SessionManager {
  private refreshTimeout: NodeJS.Timeout | null = null;

  async initialize() {
    // Drain any legacy AsyncStorage tokens into the keychain before anything
    // else reads them. Idempotent.
    await migrateLegacyTokensIfNeeded();
    await this.setupTokenRefresh();
  }

  /**
   * Public method for SplashScreen and other callers to trigger a token refresh.
   * Returns true if refresh succeeded, false otherwise.
   */
  async tryRefreshToken(): Promise<boolean> {
    return this.refreshToken();
  }

  /**
   * Check if the current token is expired or expiring soon.
   * Returns true if token needs refresh.
   */
  async isTokenExpiredOrExpiring(): Promise<boolean> {
    const token = await getAuthToken();
    if (!token) return true;

    const decoded = this.parseJwt(token);
    if (!decoded) return true;

    const expiresAt = decoded.exp * 1000;
    const timeUntilExpiry = expiresAt - Date.now();

    return timeUntilExpiry < REFRESH_THRESHOLD;
  }

  private async setupTokenRefresh() {
    const token = await getAuthToken();
    if (!token) return;

    try {
      const decoded = this.parseJwt(token);
      if (!decoded) return;

      const expiresAt = decoded.exp * 1000;
      const timeUntilExpiry = expiresAt - Date.now();

      if (timeUntilExpiry < REFRESH_THRESHOLD) {
        // Token is expired or about to expire — refresh now
        sessionLogger.info('Token expired or expiring soon, refreshing...');
        const success = await this.refreshToken();
        if (!success) {
          sessionLogger.warn('Token refresh failed during setup');
        }
      } else {
        // Schedule refresh for 5 minutes before expiry
        const refreshIn = timeUntilExpiry - REFRESH_THRESHOLD;
        sessionLogger.info(`Token valid, scheduling refresh in ${Math.round(refreshIn / 60000)} minutes`);
        this.scheduleTokenRefresh(refreshIn);
      }
    } catch (error) {
      sessionLogger.error('Error setting up token refresh:', error);
    }
  }

  /**
   * Refresh the JWT using the device trust token (30-day).
   * Returns true on success, false on failure.
   *
   * Note: the legacy AsyncStorage `@user_id` key remains non-sensitive and is
   * read directly here — moving it to the keychain would require a biometric
   * prompt every ~55 minutes when the refresh fires.
   */
  private async refreshToken(): Promise<boolean> {
    try {
      const userId = await AsyncStorage.getItem('@user_id');
      const deviceTrustToken = await getDeviceTrustToken();

      if (!userId || !deviceTrustToken) {
        sessionLogger.warn('No userId or device trust token available for refresh');
        return false;
      }

      sessionLogger.info('Refreshing token via device trust...');

      // The session being renewed goes along (it may have just expired):
      // the server renews a live session, never one that was signed out.
      const currentToken = await getAuthToken();
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(currentToken ? { Authorization: `Bearer ${currentToken}` } : {}),
        },
        body: JSON.stringify({ userId, deviceTrustToken }),
      });

      if (!response.ok) {
        sessionLogger.error('Token refresh request failed:', response.status);
        return false;
      }

      const data = await response.json();

      if (!data.success) {
        sessionLogger.warn('Token refresh returned success=false:', data.message);
        return false;
      }

      await setAuthToken(data.access_token);

      sessionLogger.info('Token refreshed successfully');

      // Schedule the next refresh
      await this.setupTokenRefresh();

      return true;
    } catch (error) {
      sessionLogger.error('Error refreshing token:', error);
      return false;
    }
  }

  private scheduleTokenRefresh(delayMs: number) {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }

    this.refreshTimeout = setTimeout(() => {
      this.refreshToken();
    }, delayMs);
  }

  parseJwt(token: string) {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      }).join(''));

      return JSON.parse(jsonPayload);
    } catch (error) {
      sessionLogger.error('Error parsing JWT:', error);
      return null;
    }
  }

  /**
   * Clean up all timers. Call on logout.
   */
  cleanup() {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
      this.refreshTimeout = null;
    }
  }

  /**
   * Drop the stored access token — used by the 401 interceptor so the next
   * app launch lands on the Login screen.
   */
  async clearAccessToken(): Promise<void> {
    await removeAuthToken();
  }
}

export const sessionManager = new SessionManager();
